import { MailOpen, Settings, Stethoscope } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

interface VetSummary {
  vetUserId: string;
  email: string;
  name: string | null;
  petCount: number;
  lastGrantedAt: string;
}

interface VetNote {
  id: string;
  petId: string;
  body: string;
  status: 'unread' | 'read' | 'replied';
  createdAt: string;
  vetEmail: string;
  vetName: string | null;
}

/**
 * /vets — staff-side cross-tenant vet directory.
 *
 * Two sections:
 *   1. Vets active at this tenant (customers have invited them) — distinct
 *      vet rows with pet count.
 *   2. Vet notes inbox — incoming messages from vets via the vet portal.
 *
 * Uses design-system Tailwind classes. Includes a link to /settings/vets
 * so staff can find where to add / manage the practice directory.
 */
export function VetDirectoryPage() {
  const [vets, setVets] = useState<VetSummary[] | null>(null);
  const [notes, setNotes] = useState<VetNote[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<'all' | 'unread'>('unread');

  async function refresh() {
    try {
      const [vRes, nRes] = await Promise.all([
        fetch('/api/vet-directory', { credentials: 'include' }).then(
          (r) => r.json() as Promise<{ items: VetSummary[] }>,
        ),
        fetch(`/api/vet-directory/notes${filter === 'unread' ? '?status=unread' : ''}`, {
          credentials: 'include',
        }).then((r) => r.json() as Promise<{ items: VetNote[] }>),
      ]);
      setVets(vRes.items ?? []);
      setNotes(nRes.items ?? []);
      setError(null);
    } catch (_err) {
      setError('Could not load the vet directory.');
    }
  }

  useEffect(() => {
    void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  async function handleMarkRead(noteId: string) {
    await fetch(`/api/vet-directory/notes/${encodeURIComponent(noteId)}`, {
      method: 'PATCH',
      credentials: 'include',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ status: 'read' }),
    });
    await refresh();
  }

  return (
    <div className="mx-auto max-w-3xl px-4 py-6 sm:px-6 space-y-8">
      {/* Page header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-medium tracking-tight">Vets</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Vets your customers have invited to view their pets&rsquo; medical records, and notes
            they&rsquo;ve sent.
          </p>
        </div>
        {/* Discoverability: link to the practice directory settings */}
        <Link
          to="/settings/vets"
          className="flex shrink-0 items-center gap-1.5 rounded-md border border-border px-3 py-1.5 text-sm text-muted-foreground hover:bg-secondary hover:text-foreground"
        >
          <Settings className="size-3.5" strokeWidth={1.75} />
          Manage vet directory
        </Link>
      </div>

      {error && (
        <div className="rounded-lg border border-destructive/20 bg-destructive/10 px-4 py-3 text-sm text-destructive">
          {error}
        </div>
      )}

      {/* Invited vets section */}
      <section className="space-y-3">
        <h2 className="flex items-center gap-2 text-sm font-semibold text-foreground">
          <Stethoscope className="size-4 text-muted-foreground" strokeWidth={1.75} />
          Invited vets
          {vets !== null && (
            <span className="num ml-1 rounded-full bg-secondary px-2 py-0.5 text-xs font-medium text-muted-foreground">
              {vets.length}
            </span>
          )}
        </h2>

        {vets === null ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : vets.length === 0 ? (
          <div className="rounded-xl border border-border bg-surface px-5 py-6 text-center">
            <Stethoscope
              className="mx-auto mb-2 size-6 text-muted-foreground/50"
              strokeWidth={1.5}
            />
            <p className="text-sm text-muted-foreground">
              No vets have been invited yet. Customers invite their vet from the pet detail page in
              the customer portal.
            </p>
            <p className="mt-2 text-xs text-muted-foreground">
              To add vets to your practice directory,{' '}
              <Link to="/settings/vets" className="text-primary underline underline-offset-2">
                go to Settings &rarr; Vets
              </Link>
              .
            </p>
          </div>
        ) : (
          <div className="rounded-xl border border-border bg-surface overflow-x-auto">
            <table className="min-w-[560px] w-full text-sm">
              <thead>
                <tr className="border-b border-border bg-secondary/50">
                  <th className="px-4 py-2.5 text-left text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
                    Vet
                  </th>
                  <th className="px-4 py-2.5 text-left text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
                    Email
                  </th>
                  <th className="px-4 py-2.5 text-left text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
                    Pets
                  </th>
                  <th className="px-4 py-2.5 text-left text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
                    Last invited
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border/60">
                {vets.map((v) => (
                  <tr key={v.vetUserId} className="hover:bg-secondary/30">
                    <td className="px-4 py-3 font-medium text-foreground">{v.name ?? '—'}</td>
                    <td className="px-4 py-3 text-muted-foreground">{v.email}</td>
                    <td className="num px-4 py-3 text-muted-foreground">{v.petCount}</td>
                    <td className="num px-4 py-3 text-muted-foreground">
                      {v.lastGrantedAt.slice(0, 10)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* Vet notes inbox section */}
      <section className="space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="flex items-center gap-2 text-sm font-semibold text-foreground">
            <MailOpen className="size-4 text-muted-foreground" strokeWidth={1.75} />
            Vet notes inbox
          </h2>
          <label className="flex items-center gap-1.5 text-sm text-muted-foreground">
            Show:
            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value as 'all' | 'unread')}
              className="rounded-md border border-border bg-background px-2 py-1 text-xs outline-none focus:ring-2 focus:ring-primary/30"
            >
              <option value="unread">Unread</option>
              <option value="all">All</option>
            </select>
          </label>
        </div>

        {notes === null ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : notes.length === 0 ? (
          <p className="text-sm text-muted-foreground">No notes.</p>
        ) : (
          <div className="space-y-2">
            {notes.map((n) => (
              <div
                key={n.id}
                className={`rounded-xl border border-border px-4 py-3 ${
                  n.status === 'unread' ? 'bg-secondary/50' : 'bg-background'
                }`}
              >
                <div className="flex items-baseline justify-between gap-4">
                  <p className="text-sm font-medium text-foreground">{n.vetName ?? n.vetEmail}</p>
                  <span className="num shrink-0 text-xs text-muted-foreground">
                    {n.createdAt.slice(0, 16)}
                  </span>
                </div>
                <p className="mt-1 whitespace-pre-line text-sm text-muted-foreground">{n.body}</p>
                <div className="mt-2.5">
                  {n.status === 'unread' ? (
                    <button
                      type="button"
                      onClick={() => void handleMarkRead(n.id)}
                      className="rounded-md bg-primary px-3 py-1 text-xs font-medium text-primary-foreground hover:opacity-90"
                    >
                      Mark read
                    </button>
                  ) : (
                    <span className="text-xs capitalize text-muted-foreground">{n.status}</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
